import { z } from 'zod';
/**
 * The content director's AI step (spec 110 §2, REQ-110-007..010, S110-3):
 * the seven tools of the `content_director` job, the loop limits and the
 * `data` of the two kinds its one write tool may create. The job definition
 * itself belongs in module 80's registry (ADR-007); the tools, the loop and
 * the guardrails are module 110's.
 */
/** The tool list, exactly (AC-110-013): six read-only tools and one write tool. */
export const CONTENT_DIRECTOR_TOOLS = [
    'getCalendar',
    'getContentMix',
    'searchMedia',
    'searchKnowledge',
    'getCampaigns',
    'getRecentPublications',
    'createInboxMessage',
];
/** The only tool that changes data; it can only write inbox messages. */
export const CONTENT_DIRECTOR_WRITE_TOOL = 'createInboxMessage';
/** The kinds the write tool may create (spec 110 §4.1); never `research_suggestion` (§4.4). */
export const DIRECTOR_AI_KINDS = ['director_suggestion', 'director_note'];
/** Loop limits (spec 110 §9 Q3 proposals). */
export const CONTENT_DIRECTOR_LIMITS = {
    /** Tool calls per run, refused ones included; the next request ends the run `tool_loop_limit` (AC-110-015). */
    maxToolCalls: 12,
    /** Excerpt of a knowledge item returned by `searchKnowledge` (AC-110-019). */
    knowledgeExcerptChars: 500,
    /** Items any read tool returns at most. */
    maxToolItems: 20,
    /** Highlights of a run's final output (`director_runs.summary.highlights`). */
    maxHighlights: 5,
    highlightChars: 200,
};
/**
 * Codes the loop returns to the model for a refused call (AC-110-014/015/017).
 * Refusals are data for the model and the AI run output, never HTTP problems.
 */
export const DIRECTOR_TOOL_REFUSALS = ['tool_not_allowed', 'invalid_arguments', 'message_limit_reached', 'invalid_reference', 'invalid_message'];
const uuid = z.uuid();
const isoDate = z.iso.date();
/** Knowledge items one message may cite. */
export const DIRECTOR_MAX_CITED_ITEMS = 10;
const citedIds = z.array(uuid).min(1).max(DIRECTOR_MAX_CITED_ITEMS);
/**
 * One factual statement of an AI message and the verified knowledge items it
 * relies on (REQ-110-009, REQ-KI-007). A statement without a source is
 * invalid; the write tool checks every cited ID is a verified item of the
 * tenant (AC-110-017).
 */
export const directorFactSchema = z.object({ text: z.string().trim().min(1).max(300), knowledge_item_ids: citedIds }).strict();
/** A campaign the director proposes; `create_campaign` is offered only with it (spec 110 §4.1). */
export const directorCampaignProposalSchema = z
    .object({ name: z.string().trim().min(1).max(120), starts_on: isoDate, ends_on: isoDate })
    .strict()
    .refine((proposal) => proposal.ends_on >= proposal.starts_on, { message: 'ends_on before starts_on', path: ['ends_on'] });
/**
 * `director_suggestion`: a topic idea of the content director. `topic` and
 * `rationale` are AI-authored content (shown marked as AI-written, spec 110
 * §6), not UI text; facts only through `facts` with their sources.
 */
export const directorSuggestionDataSchema = z
    .object({
    property_id: uuid,
    topic: z.string().trim().min(1).max(200),
    rationale: z.string().trim().min(1).max(1000),
    proposed_date: isoDate,
    pillar_id: uuid.nullable().default(null),
    apartment_id: uuid.nullable().default(null),
    /** E470 (prompt v4): entries the topic is about, IDs from the context's `entries`; the accepted idea is about them. */
    entry_ids: z.array(uuid).max(5).default([]),
    campaign_id: uuid.nullable().default(null),
    media_asset_ids: z.array(uuid).max(10).default([]),
    facts: z.array(directorFactSchema).max(5).default([]),
    campaign_proposal: directorCampaignProposalSchema.nullable().default(null),
})
    .strict();
/** `director_note`: an observation of the content director (info or warning). */
export const directorNoteDataSchema = z
    .object({
    property_id: uuid.nullable().default(null),
    text: z.string().trim().min(1).max(1000),
    facts: z.array(directorFactSchema).max(5).default([]),
})
    .strict();
/** The routines whose settings can switch the AI step on (`ai_director_daily`, `ai_director_weekly`). */
export const DIRECTOR_AI_ROUTINES = ['daily', 'weekly'];
/** `params` of a `content_director` run (module 80's `ai_runs.params`); the tenant comes from the run, never from here. */
export const contentDirectorParamsSchema = z
    .object({
    director_run_id: uuid,
    routine: z.enum(DIRECTOR_AI_ROUTINES),
    period_start: isoDate,
    period_end: isoDate,
    /** `director_settings.max_messages_per_ai_run` at the time the run was started. */
    max_messages: z.number().int().min(1).max(10),
})
    .strict();
const callOutcome = z.enum(['ok', ...DIRECTOR_TOOL_REFUSALS]);
/**
 * `output` of a `content_director` run: the highlights of the final step and
 * every tool call with its outcome, refusals included (AC-110-014). Written by
 * the loop, not by the model.
 */
export const contentDirectorOutputSchema = z
    .object({
    highlights: z.array(z.string().min(1).max(CONTENT_DIRECTOR_LIMITS.highlightChars)).max(CONTENT_DIRECTOR_LIMITS.maxHighlights),
    calls: z.array(z.object({ tool: z.string().max(64), outcome: callOutcome }).strict()).max(CONTENT_DIRECTOR_LIMITS.maxToolCalls),
    created_message_ids: z.array(uuid).max(10),
})
    .strict();
/** Every knowledge item ID a message's facts cite, deduplicated, in order of appearance. */
export function citedKnowledgeItemIds(data) {
    return [...new Set(data.facts.flatMap((fact) => fact.knowledge_item_ids))];
}
//# sourceMappingURL=director-ai.js.map